import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { subscribeToEmotes, getEmojis, subscribeToThemes, subscribeToSystemConfig } from '../services/firestore.js';
import { Smile, Zap, Lock } from 'lucide-react';

export default function EmotePicker({ onSelect, onClose, user, userTier = 'global', mode = 'chat', style = {} }) {
    const [activeTab, setActiveTab] = useState('custom'); // 'standard' | 'custom'
    const [customEmotes, setCustomEmotes] = useState([]);
    const [legacyEmojis, setLegacyEmojis] = useState([]);
    const [themes, setThemes] = useState([]);
    const [activeThemeId, setActiveThemeId] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Load Legacy Emojis (Standard)
        const loadLegacy = async () => {
            try {
                const emojis = await getEmojis('superSubscriber'); // Fetch all
                setLegacyEmojis(emojis);
            } catch (e) {
                console.error("Failed to load legacy emojis", e);
                setLegacyEmojis([]);
            }
        };
        loadLegacy();

        // Subscribe to Custom Emotes & Themes
        const unsubEmotes = subscribeToEmotes(setCustomEmotes);
        const unsubThemes = subscribeToThemes(setThemes);
        const unsubConfig = subscribeToSystemConfig((config) => setActiveThemeId(config.activeThemeId || null));

        setLoading(false);

        return () => {
            unsubEmotes();
            unsubThemes();
            unsubConfig();
        };
    }, []);

    // Resolve Active Theme
    const activeTheme = activeThemeId ? themes.find(t => t.id === activeThemeId) : null;

    const canAccess = (emoteTier) => {
        // Admins see everything
        if (user?.isSuperAdmin || user?.isAdmin) return true;

        if (emoteTier === 'global') return true;
        if (emoteTier === 'subscriber' && ['subscriber', 'super_subscriber', 'superSubscriber', 'mega_fan'].includes(userTier)) return true;
        if ((emoteTier === 'super_fan' || emoteTier === 'superSubscriber') && ['super_subscriber', 'superSubscriber', 'mega_fan'].includes(userTier)) return true;
        if (emoteTier === 'mega_fan' && userTier === 'mega_fan') return true;
        return false;
    };

    const tierConfig = {
        global: { label: '🌍 Global (Free)', color: '#10b981' },
        subscriber: { label: '💎 Supporter', color: '#ec4899' },
        super_fan: { label: '👑 Super Fan', color: '#8b5cf6' },
        superSubscriber: { label: '👑 Super Fan', color: '#8b5cf6' },
        mega_fan: { label: '🦄 Mega Fan', color: '#FFD700' }
    };

    const groupedLegacy = {
        global: legacyEmojis.filter(e => e.tier === 'global'),
        subscriber: legacyEmojis.filter(e => e.tier === 'subscriber'),
        superSubscriber: legacyEmojis.filter(e => e.tier === 'superSubscriber')
    };

    const groupedCustom = {
        global: customEmotes.filter(e => e.tier === 'global'),
        subscriber: customEmotes.filter(e => e.tier === 'subscriber'),
        super_fan: customEmotes.filter(e => e.tier === 'super_fan' || e.tier === 'superSubscriber'),
        mega_fan: customEmotes.filter(e => e.tier === 'mega_fan')
    };

    // Gamification Logic
    const badgeCount = user?.badges ? user.badges.filter(b => b.unlocked).length : 0;
    // 0 badges = 2 emotes, 1 badge = 4 emotes, 2 badges = 6 emotes, 3 badges = 8 emotes, 4+ badges = ALL (9999)
    const unlockLimit = badgeCount >= 4 ? 9999 : 2 + (badgeCount * 2);

    const renderEmojiGroup = (emojis, tierKey, isCustom = false, forceRender = false) => {
        if (!forceRender && emojis.length === 0) return null;

        const config = tierConfig[tierKey] || { label: tierKey, color: '#999' };

        // If forceRender is true, we ensure we satisfy the "mix" of emotes + placeholders
        // We simulate a grid of 10 slots (unless we have more than 10 emotes)
        const totalSlots = Math.max(10, emojis.length);
        const placeholdersNeeded = Math.max(0, totalSlots - emojis.length);

        const displayItems = [
            ...emojis,
            ...Array(placeholdersNeeded).fill({ isPlaceholder: true })
        ].map((item, idx) => item.isPlaceholder ? { ...item, id: `placeholder-${tierKey}-${idx}` } : item);

        return (
            <div key={tierKey} style={{ marginBottom: '20px' }}>
                <div style={{
                    fontSize: '11px',
                    fontWeight: 'bold',
                    color: config.color,
                    marginBottom: '10px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                }}>
                    {config.label}
                    {/* Progress Indicator for this tier - Only show if current user has badges < 4 AND there are actual emojis */}
                    {badgeCount < 4 && emojis.length > 0 && (
                        <span style={{ marginLeft: '10px', fontSize: '9px', opacity: 0.6, textTransform: 'none' }}>
                            (Unlocked: {Math.min(emojis.length, unlockLimit)}/{emojis.length})
                        </span>
                    )}
                </div>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: '8px'
                }}>
                    {displayItems.map((item, index) => {
                        if (item.isPlaceholder) {
                            return (
                                <button
                                    key={`${tierKey}-placeholder-${index}`}
                                    disabled
                                    style={{
                                        background: 'rgba(0,0,0,0.2)',
                                        border: '1px dashed rgba(255,255,255,0.05)',
                                        borderRadius: '8px',
                                        height: '50px',
                                        cursor: 'default',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: 0.3
                                    }}
                                >
                                    <Lock size={12} color="#666" />
                                </button>
                            );
                        }

                        const accessibleTier = canAccess(item.tier);
                        const isBadgeLocked = index >= unlockLimit;
                        const fullyUnlocked = accessibleTier && !isBadgeLocked;

                        const valueToReturn = isCustom
                            ? (mode === 'chat' ? item.code : item.id)
                            : item.emoji;

                        // Theme Resolution
                        const displayUrl = (isCustom && activeTheme && activeTheme.overrides && activeTheme.overrides[item.id])
                            ? activeTheme.overrides[item.id]
                            : item.url;

                        return (
                            <button
                                key={item.id}
                                type="button"
                                onClick={() => {
                                    if (fullyUnlocked) {
                                        onSelect(valueToReturn);
                                        if (onClose) onClose();
                                    } else if (accessibleTier && isBadgeLocked) {
                                        alert(`🔒 Unlock more badges to earn this emote! You have ${badgeCount}/4 badges needed for full access.`);
                                    }
                                }}
                                disabled={!accessibleTier}
                                title={fullyUnlocked ? item.name : (accessibleTier && isBadgeLocked ? `🔒 Locked: Earn ${Math.ceil((index + 1 - 2) / 2) - badgeCount} more badge(s)` : `🔒 ${item.name}`)}
                                style={{
                                    background: fullyUnlocked ? 'var(--chat-bg-in)' : 'rgba(0,0,0,0.1)',
                                    border: '1px solid var(--border)',
                                    borderRadius: '8px',
                                    padding: '5px',
                                    height: '50px',
                                    cursor: fullyUnlocked ? 'pointer' : (accessibleTier ? 'help' : 'not-allowed'),
                                    transition: 'all 0.2s ease',
                                    position: 'relative',
                                    opacity: fullyUnlocked ? 1 : 0.4,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    filter: (!fullyUnlocked && isBadgeLocked) ? 'grayscale(100%)' : 'none',
                                    color: 'var(--text-main)' // Ensure emoji text color is correct
                                }}
                            >
                                {displayUrl ? (
                                    <img
                                        src={displayUrl}
                                        alt={item.name}
                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                    />
                                ) : (
                                    <span style={{ fontSize: '24px' }}>{item.emoji}</span>
                                )}
                                {!fullyUnlocked && (
                                    <div style={{
                                        position: 'absolute', top: '2px', right: '2px',
                                        width: '16px', height: '16px',
                                        background: 'rgba(0,0,0,0.6)',
                                        borderRadius: '50%',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        <Lock size={10} color={accessibleTier ? "#f59e0b" : "#999"} />
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    return createPortal(
        <div style={{ position: 'fixed', zIndex: 9999, top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            {/* Backdrop */}
            <div
                onClick={(e) => { e.stopPropagation(); onClose && onClose(); }}
                style={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: 'var(--backdrop)',
                    zIndex: -1,
                    pointerEvents: 'auto'
                }}
            />

            {/* Panel */}
            <div className="glass-panel"
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: '380px', // slightly wider
                    height: '550px', // Taller
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    position: 'relative',
                    // Background handled by glass-panel but we ensure contrast
                    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                    borderRadius: '20px',
                    pointerEvents: 'auto',
                    border: '1px solid var(--border)',
                    ...style // Allow size override if needed
                }}>

                {/* Header with Tabs */}
                <div style={{
                    display: 'flex',
                    borderBottom: '1px solid var(--border)',
                    background: 'var(--bg-card-hover)'
                }}>
                    <button
                        type="button"
                        onClick={() => setActiveTab('custom')}
                        style={{
                            flex: 1, padding: '16px',
                            background: activeTab === 'custom' ? 'var(--bg-card)' : 'transparent',
                            border: 'none', color: activeTab === 'custom' ? 'var(--text-main)' : 'var(--text-muted)',
                            cursor: 'pointer', display: 'flex', justifyContent: 'center', gap: '8px', alignItems: 'center',
                            fontSize: '14px', fontWeight: 'bold',
                            borderBottom: activeTab === 'custom' ? '3px solid #eab308' : '3px solid transparent', // Keep custom color for custom tab
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <Zap size={18} color={activeTab === 'custom' ? '#eab308' : 'var(--text-muted)'} /> Muse Emotes
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('standard')}
                        style={{
                            flex: 1, padding: '16px',
                            background: activeTab === 'standard' ? 'var(--bg-card)' : 'transparent',
                            border: 'none', color: activeTab === 'standard' ? 'var(--text-main)' : 'var(--text-muted)',
                            cursor: 'pointer', display: 'flex', justifyContent: 'center', gap: '8px', alignItems: 'center',
                            fontSize: '14px', fontWeight: 'bold',
                            borderBottom: activeTab === 'standard' ? '3px solid var(--primary)' : '3px solid transparent',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <Smile size={18} /> Emojis
                    </button>
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading...</div>
                    ) : (
                        activeTab === 'standard' ? (
                            <>
                                {groupedLegacy.global.length > 0 && renderEmojiGroup(groupedLegacy.global, 'global')}
                                {groupedLegacy.subscriber.length > 0 && renderEmojiGroup(groupedLegacy.subscriber, 'subscriber')}
                                {groupedLegacy.superSubscriber.length > 0 && renderEmojiGroup(groupedLegacy.superSubscriber, 'superSubscriber')}
                                {legacyEmojis.length === 0 && <div style={{ textAlign: 'center', color: '#666', marginTop: '20px' }}>No emojis found.</div>}
                            </>
                        ) : (
                            <>
                                {renderEmojiGroup(groupedCustom.global, 'global', true, true)}
                                {renderEmojiGroup(groupedCustom.subscriber, 'subscriber', true, true)}
                                {renderEmojiGroup(groupedCustom.super_fan, 'super_fan', true, true)}
                                {(user?.isSuperAdmin || user?.isAdmin || userTier === 'mega_fan') &&
                                    renderEmojiGroup(groupedCustom.mega_fan, 'mega_fan', true, true)
                                }
                            </>
                        )
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
